import React, { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faPlus, faTrash, faEdit, faBuilding, faExchangeAlt, faCheck } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Predefined list of MSP partners
const AVAILABLE_MSP_PARTNERS = [
  {
    id: 'msp1',
    name: 'TechGuard Solutions',
    type: 'Managed Service Provider',
    description: 'Enterprise-focused MSP specializing in cybersecurity and compliance',
    logo: 'https://via.placeholder.com/50?text=TS'
  },
  {
    id: 'msp2',
    name: 'CloudWave Systems',
    type: 'Managed Service Provider',
    description: 'Cloud infrastructure and management specialists',
    logo: 'https://via.placeholder.com/50?text=CS'
  },
  {
    id: 'msp3',
    name: 'Secure IT Partners',
    type: 'Managed Service Provider',
    description: 'Security-focused MSP with 24/7 monitoring and support',
    logo: 'https://via.placeholder.com/50?text=SP'
  },
  {
    id: 'msp4',
    name: 'Horizon Managed Services',
    type: 'Managed Service Provider',
    description: 'Full-service IT management for small to medium businesses',
    logo: 'https://via.placeholder.com/50?text=HM'
  },
  {
    id: 'msp5',
    name: 'Digital Guardian MSP',
    type: 'Managed Service Provider',
    description: 'Specialized in compliance and regulatory requirements',
    logo: 'https://via.placeholder.com/50?text=DG'
  }
];

export default function PartnersSection() {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState("partners");
  const [selectedMSP, setSelectedMSP] = useState<string | null>(null);
  const [isChanging, setIsChanging] = useState(false);
  const [partnerToChange, setPartnerToChange] = useState<{id: string, name: string, type: string} | null>(null);

  const handleEditPartner = (partner: {id: string, name: string, type: string}) => {
    setPartnerToChange(partner);
    setActiveTab("changePartner");
  };

  const handleChangePartner = () => {
    if (!selectedMSP || !partnerToChange) return;
    
    setIsChanging(true);
    // Simulate API call to change partner
    setTimeout(() => {
      setIsChanging(false);
      // Reset selection after successful change
      setSelectedMSP(null);
      setPartnerToChange(null);
      setActiveTab("partners");
      // In a real implementation, you would update the current partners list
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Partners</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">Manage partners associated with your tenancy.</p>
        
        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 hidden">
            <TabsTrigger value="partners">Current Partners</TabsTrigger>
            <TabsTrigger value="changePartner">Change Partner</TabsTrigger>
          </TabsList>
          
          {/* Partners List Tab */}
          <TabsContent value="partners" className="space-y-6">
            <div className="bg-muted/30 p-6 rounded-lg border border-border">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-500/10 p-3 rounded-full">
                    <FontAwesomeIcon icon={faBuilding} className="text-blue-600" size="lg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-foreground">Acme Corporation</h3>
                    <p className="text-sm text-muted-foreground">Technology Partner</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    className="p-2 bg-background hover:bg-accent text-foreground rounded-lg transition-colors"
                    onClick={() => handleEditPartner({id: "acme", name: "Acme Corporation", type: "Technology Partner"})}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button className="p-2 bg-background hover:bg-red-500/10 text-red-500 rounded-lg transition-colors">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Partner Name
                    </label>
                    <p className="text-sm text-foreground">Acme Corporation</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Partner Type
                    </label>
                    <p className="text-sm text-foreground">Technology Partner</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Contact Person
                    </label>
                    <p className="text-sm text-foreground">John Smith</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Contact Email
                    </label>
                    <p className="text-sm text-foreground">john.smith@acme.com</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Contact Phone
                    </label>
                    <p className="text-sm text-foreground">+1 (555) 123-4567</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Partnership Since
                    </label>
                    <p className="text-sm text-foreground">January 15, 2023</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-muted/30 p-6 rounded-lg border border-border">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500/10 p-3 rounded-full">
                    <FontAwesomeIcon icon={faHandshake} className="text-green-600" size="lg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-foreground">GlobalTech Solutions</h3>
                    <p className="text-sm text-muted-foreground">Implementation Partner</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    className="p-2 bg-background hover:bg-accent text-foreground rounded-lg transition-colors"
                    onClick={() => handleEditPartner({id: "globaltech", name: "GlobalTech Solutions", type: "Implementation Partner"})}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button className="p-2 bg-background hover:bg-red-500/10 text-red-500 rounded-lg transition-colors">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Partner Name
                    </label>
                    <p className="text-sm text-foreground">GlobalTech Solutions</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Partner Type
                    </label>
                    <p className="text-sm text-foreground">Implementation Partner</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Contact Person
                    </label>
                    <p className="text-sm text-foreground">Sarah Johnson</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Contact Email
                    </label>
                    <p className="text-sm text-foreground">sarah.j@globaltech.com</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Contact Phone
                    </label>
                    <p className="text-sm text-foreground">+1 (555) 987-6543</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Partnership Since
                    </label>
                    <p className="text-sm text-foreground">March 22, 2023</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Change Partner Tab */}
          <TabsContent value="changePartner" className="space-y-6">
            <div className="bg-muted/30 p-6 rounded-lg border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faExchangeAlt} className="text-primary" />
                {partnerToChange ? `Change ${partnerToChange.type}` : 'Change Partner'}
              </h3>
              
              <p className="text-sm text-muted-foreground mb-6">
                {partnerToChange 
                  ? `Select a new MSP partner to replace ${partnerToChange.name}. Your data and settings will remain intact.` 
                  : 'Select a new MSP partner from the list below. Your data and settings will remain intact.'}
              </p>
              
              <div className="space-y-6">
                {AVAILABLE_MSP_PARTNERS.map((msp) => (
                  <div 
                    key={msp.id}
                    className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                      selectedMSP === msp.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border bg-background hover:bg-muted/20'
                    }`}
                    onClick={() => setSelectedMSP(msp.id)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <img 
                          src={msp.logo} 
                          alt={`${msp.name} logo`} 
                          className="w-12 h-12 rounded-md object-cover"
                        />
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center justify-between">
                          <h4 className="text-base font-medium text-foreground">{msp.name}</h4>
                          {selectedMSP === msp.id && (
                            <span className="text-primary">
                              <FontAwesomeIcon icon={faCheck} />
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{msp.type}</p>
                        <p className="text-sm text-muted-foreground mt-2">{msp.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex justify-between">
                <button
                  className="px-4 py-2 rounded-lg font-medium transition-colors bg-muted text-muted-foreground hover:bg-muted/80"
                  onClick={() => {
                    setPartnerToChange(null);
                    setSelectedMSP(null);
                    setActiveTab("partners");
                  }}
                >
                  Cancel
                </button>
                
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    !selectedMSP || isChanging
                      ? 'bg-primary/50 text-primary-foreground cursor-not-allowed'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
                  disabled={!selectedMSP || isChanging}
                  onClick={handleChangePartner}
                >
                  {isChanging ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-b-2 border-current rounded-full"></div>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faExchangeAlt} />
                      {partnerToChange ? `Replace ${partnerToChange.type}` : 'Change to Selected MSP'}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
